import React, {useState, useEffect} from 'react';
import {ScrollView, View, Image, Text, Pressable, Modal} from 'react-native';
import {ObjectId, UUID} from 'bson';
import {useIsFocused} from '@react-navigation/native';
import {useToast} from 'react-native-toast-notifications';
import SearchBox from '../Shared/SearchBox';
import DynamicTextBox from '../DynamicControls/DynamicTextBox';
import DynamicSelect from '../DynamicControls/DynamicSelect';
import DynamicSegmentedControl from '../DynamicControls/DynamicSegmentedControl';
import Icon from '../Shared/Icon';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import {Images} from '../../Constants/Images';
import Colors from '../../Styles/Shared/Colors';
import {RegexValidation} from '../../Constants/General';
import {ListDataTypes} from '../../Constants/ListDataTypes';
import {
  OIICS_LINKS,
  PERMISSIONS,
  STORAGE_KEYS,
} from '../../Constants/AppConstants';
import {Icons} from '../../Constants/Icons';
import CustomPressable from '../Shared/CustomPressable';
import {StorageKeyType} from '../../Types/StorageTypes';
import DynamicCheckBox from '../DynamicControls/DynamicCheckBox';
import {Task} from '../../Models/PowerSyncModels/models';
import {TaskType} from '../../Models/PowerSyncModels/models';
import {
  CreateListDataDTO,
  GenericListItem,
  MongoIdSqlId,
  UpdateListDataDTO,
} from '../../Types/DtoTypes';
import {usePermission} from '../../Providers/PermissionProvider';
import {IncidentClassification} from '../../Models/PowerSyncModels/models';
import {IncidentType} from '../../Models/PowerSyncModels/models';
import {SeverityLevel} from '../../Models/PowerSyncModels/models';
import {IncidentProbability} from '../../Models/PowerSyncModels/models';
import DynamicLinkText from '../DynamicControls/DynamicLinkText';
import {usePowerSync} from '@powersync/react';
import {usePowerSyncUser} from '../../Providers/PowerSyncUserProvider';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import usePowerSyncWriteData from '../../Hooks/usePowerSyncWriteData';
import {ProjectSitesStyleSheet} from '../../Styles/ProjectSiteStyles';
import Paginator from '../Shared/Paginator';

export declare type EditListManagementProps = {
  navigation: any;
  listName: string;
  listLabel: string;
  listType: string;
  isReadOnly: boolean;
};

const EditListManagement = (
  props: EditListManagementProps,
): React.ReactElement => {
  const [search, setSearch] = useState('');
  const [records, setRecords] = useState<GenericListItem[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<GenericListItem | null>(
    null,
  );
  const [showInactive, setShowInactive] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [showModalErrors, setShowModalErrors] = useState(false);
  const [modalRecord, setModalRecord] = useState<GenericListItem | null>(null);
  const [addingRecord, setAddingRecord] = useState(false);

  const [sortOrderValue, setSortOrderValue] = useState('');
  const [masterSource, setMasterSource] = useState('');
  const [masterLabel, setMasterLabel] = useState('');
  const [masterValue, setMasterValue] = useState('');
  const [itemValue, setItemValue] = useState('');
  const [nameError, setNameError] = useState('');
  const [listPageData, setListPageData] = useState<GenericListItem[]>([]);

  const DISPLAY_INCREMENT = 5;

  const {
    animalTypes,
    certificationTypes,
    classificationUnits,
    crews,
    departments,
    dispositions,
    documentTypes,
    hazardTypes,
    incidentClassifications,
    incidentProbabilities,
    incidentTypes,
    jobs,
    mechanisms,
    locations,
    locationTypes,
    OIICS_BodyParts,
    OIICS_Natures,
    OIICS_Sources,
    peopleTypes,
    severityLevels,
    shifts,
    tasks,
    taskTypes,
    trainingTypes,
  } = usePowerSyncData();

  const {
    updateListData,
    createListData,
    createListDataTask,
    updateListDataTask,
  } = usePowerSyncWriteData();

  const user = usePowerSyncUser();

  const {hasPermission} = usePermission();
  const [canUpsert, setCanUpsert] = useState(false);

  const isFocused = useIsFocused();
  const toast = useToast();

  useEffect(() => {
    let permission = hasPermission(PERMISSIONS.LISTMANAGEMENT_UPSERT);
    setCanUpsert(permission);
  }, []);

  useEffect(() => {
    if (isFocused) fetchRecords();
  }, [isFocused]);

  const fetchRecords = async (
    replaceRecord: CreateListDataDTO | UpdateListDataDTO | null = null,
  ) => {
    let orgId = user.getUserInfo().orgId.toString();
    let mappedRecords: GenericListItem[] = [];

    if (props.listName == ListDataTypes.ANIMALTYPES) {
      let itemsArray = animalTypes.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.BODYPARTS) {
      let itemsArray = OIICS_BodyParts;
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: 0,
        partition: '',
        isActive: true,
        isDeleted: false,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.CERTIFICATIONTYPES) {
      let itemsArray = certificationTypes.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.CLASSIFICATIONUNITS) {
      let itemsArray = classificationUnits;
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: 0,
        partition: orgId.toString(),
        isActive: true,
        isDeleted: false,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.CREWS) {
      let itemsArray = crews.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.DEPARTMENTS) {
      let itemsArray = departments.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.DISPOSTIONS) {
      let itemsArray = dispositions.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.DOCUMENTTYPES) {
      let itemsArray = documentTypes.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.HAZARDTYPES) {
      let itemsArray = hazardTypes.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.INCIDENTCLASSIFICATIONS) {
      let itemsArray = incidentClassifications.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.INCIDENTPROBABILITIES) {
      let itemsArray = incidentProbabilities.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.INCIDENTTYPES) {
      let itemsArray = incidentTypes.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.JOBS) {
      let itemsArray = jobs.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.MECHANISMS) {
      let itemsArray = mechanisms.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.LOCATIONS) {
      let itemsArray = locations.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.LOCATIONTYPES) {
      let itemsArray = locationTypes.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.NATURES) {
      let itemsArray = OIICS_Natures;
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: 0,
        partition: '',
        isActive: true,
        isDeleted: false,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.PEOPLETYPES) {
      let itemsArray = peopleTypes.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.SEVERITYLEVELS) {
      let itemsArray = severityLevels.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.SHIFTS) {
      let itemsArray = shifts.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.SOURCES) {
      let itemsArray = OIICS_Sources;
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: 0,
        partition: '',
        isActive: true,
        isDeleted: false,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.TASKS) {
      let itemsArray = tasks.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.TASKTYPES) {
      let itemsArray = taskTypes.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    } else if (props.listName == ListDataTypes.TRAININGTYPES) {
      let itemsArray = trainingTypes.filter(x => !x.isDeleted);
      mappedRecords = itemsArray.map(x => ({
        _id: x._id,
        name: x.name,
        sortOrder: x.sortOrder,
        partition: x.partition,
        isActive: x.isActive,
        isDeleted: x.isDeleted,
        SQLServerId: x.SQLServerId ?? '',
      }));
    }

    mappedRecords.forEach((x, i, arr) => {
      if (replaceRecord && x._id == replaceRecord?.id)
        //Update record manually as it may not be updated
        arr[i] = {
          ...x,
          isActive: replaceRecord.isActive,
          name: replaceRecord.name,
          sortOrder: replaceRecord.sortOrder,
        };
    });
    mappedRecords = sortRecords(mappedRecords);
    setRecords(mappedRecords);

    if (props.listType == 'master-detail') {
      if (props.listName == ListDataTypes.TASKS) {
        setMasterSource(ListDataTypes.TASKTYPES);
        setMasterLabel('Task Type');
      }
      if (props.listName == ListDataTypes.INCIDENTCLASSIFICATIONS) {
        setMasterSource(ListDataTypes.INCIDENTTYPES);
        setMasterLabel('Incident Type');
      }
    }
  };

  function getMasterLabel(masterValue: string): string {
    if (props.listName == ListDataTypes.TASKS) {
      let taskTypeObj = taskTypes.find(
        x => x.SQLServerId.toLowerCase() == masterValue?.toLowerCase(),
      );

      if (taskTypeObj) return taskTypeObj.name;
    } else if (props.listName == ListDataTypes.INCIDENTCLASSIFICATIONS) {
      let incidentTypeObj = incidentClassifications.find(
        x => x.SQLServerId.toLowerCase() == masterValue?.toLowerCase(),
      );

      if (incidentTypeObj) return incidentTypeObj.name;
    }

    return '';
  }

  function sortRecords(itemsToSort: GenericListItem[]): GenericListItem[] {
    return itemsToSort.sort(
      (a, b) =>
        a.sortOrder - b.sortOrder ||
        (a.name > b.name ? 1 : a.name < b.name ? -1 : 0),
    );
  }

  function goBack() {
    props.navigation.pop();
  }

  function addRecord(): void {
    let orgId = user.getUserInfo().orgId.toString();

    let newRecord: GenericListItem = {
      _id: new ObjectId().toString(),
      name: '',
      sortOrder: 0,
      partition: orgId,
      isActive: true,
      isDeleted: false,
      SQLServerId: new UUID().toString().toUpperCase(),
    };

    setMasterValue('');
    setItemValue('');

    setAddingRecord(true);
    setModalRecord(newRecord);
    setSortOrderValue('0');
    setShowModalErrors(false);
    setShowModal(true);
  }

  function viewRecord(): void {
    if (props.listType == 'master-detail') {
      if (props.listName == ListDataTypes.TASKS) {
        let taskObj = tasks.find(x => x._id === selectedRecord?._id);

        if (taskObj && taskObj.taskTypeSQLServerId) {
          let taskTypeObj = taskTypes.find(
            x =>
              x.SQLServerId.toLowerCase() ==
              taskObj!.taskTypeSQLServerId.toLowerCase(),
          );

          if (taskTypeObj) {
            let mongoIdSqlId: MongoIdSqlId = {
              mongoId: taskTypeObj._id,
              label: taskTypeObj.name,
              SQLServerId: taskTypeObj.SQLServerId,
            };

            setMasterValue(JSON.stringify(mongoIdSqlId));
          }
        }
      } else if (props.listName == ListDataTypes.INCIDENTCLASSIFICATIONS) {
        let incidentClassificationsObj = incidentClassifications.find(
          x => x._id === selectedRecord?._id,
        );

        if (
          incidentClassificationsObj &&
          incidentClassificationsObj.incidentTypeSQLServerId
        ) {
          let incidentTypeObj = incidentTypes.find(
            x =>
              x.SQLServerId.toLowerCase() ==
              incidentClassificationsObj!.incidentTypeSQLServerId?.toLowerCase(),
          );

          if (incidentTypeObj) {
            let mongoIdSqlId: MongoIdSqlId = {
              mongoId: incidentTypeObj._id,
              label: incidentTypeObj.name,
              SQLServerId: incidentTypeObj.SQLServerId,
            };

            setMasterValue(JSON.stringify(mongoIdSqlId));
          }
        }
      }
    } else {
      setMasterValue('');
    }

    if (props.listType == 'name-value') {
      if (props.listName == ListDataTypes.SEVERITYLEVELS) {
        let severityLevelObj = severityLevels.find(
          r => r._id === selectedRecord?._id,
        );
        setItemValue(severityLevelObj?.value ?? '');
      } else if (props.listName == ListDataTypes.INCIDENTPROBABILITIES) {
        let incidentProbabilityObj = incidentProbabilities.find(
          r => r._id === selectedRecord?._id,
        );
        setItemValue(incidentProbabilityObj?.value ?? '');
      }
    } else {
      setItemValue('');
    }

    setAddingRecord(false);
    setModalRecord(selectedRecord);
    setSortOrderValue(selectedRecord?.sortOrder.toString() ?? '');
    setShowModalErrors(false);
    setShowModal(true);
  }

  function closeModal(): void {
    setAddingRecord(false);
    setModalRecord(null);
    setShowModalErrors(false);
    setShowModal(false);
  }

  async function onModalRecordChange(
    propName: string,
    propValue: string,
  ): Promise<void> {
    let rec = {...modalRecord} as GenericListItem;

    if (propName === 'name') rec.name = propValue;
    else if (propName === 'isActive') rec.isActive = propValue === 'Yes';

    setModalRecord(rec);
    setNameError('');
  }

  async function saveRecord(): Promise<void> {
    if (!modalRecord || !modalRecord.name) {
      setShowModalErrors(true);
      return;
    }
    let newRecord = null;
    let duplicateRecord = records.find(
      x =>
        x.name.trim().toLowerCase() == modalRecord.name.trim().toLowerCase() &&
        x._id != modalRecord._id,
    );

    if (duplicateRecord) {
      setNameError('Name already exists');
      return;
    }

    let isSortOrderValid = false;
    if (sortOrderValue) {
      let sortOrderInt = parseInt(sortOrderValue);
      if (!isNaN(sortOrderInt)) {
        modalRecord.sortOrder = sortOrderInt;
        isSortOrderValid = true;
      }
    }
    if (!isSortOrderValid) {
      setShowModalErrors(true);
      return;
    }

    let orgId = user.getUserInfo().orgId.toString();
    modalRecord.name = modalRecord.name.trim();

    // Save master-detail
    if (props.listType == 'master-detail') {
      if (!masterValue) {
        setShowModalErrors(true);
        return;
      }

      let masterSQLServerId = '';
      try {
        let mongoIdSqlId = JSON.parse(masterValue) as MongoIdSqlId;
        masterSQLServerId = mongoIdSqlId.SQLServerId ?? '';
      } catch (error) {}
      if (props.listName == ListDataTypes.TASKS) {
        let realmTask: Task | undefined = undefined;
        if (addingRecord) {
          realmTask = {
            _id: modalRecord._id,
            id: modalRecord._id,
            name: modalRecord.name,
            sortOrder: modalRecord.sortOrder,
            partition: orgId,
            isActive: modalRecord.isActive,
            isDeleted: false,
            SQLServerId: modalRecord.SQLServerId,
            dataHubVersion: Math.round(Date.now() / 1000),
            taskTypeSQLServerId: masterSQLServerId,
          };

          await createListDataTask(realmTask);
        } else {
          realmTask = tasks.find(r => r._id === modalRecord._id);

          if (realmTask) {
            realmTask.name = modalRecord.name;
            realmTask.sortOrder = modalRecord.sortOrder;
            realmTask.isActive = modalRecord.isActive;
            realmTask.dataHubVersion = Math.round(Date.now() / 1000);
            realmTask.taskTypeSQLServerId = masterSQLServerId;
          }

          await updateListDataTask(realmTask!);
        }
      } else if (props.listName == ListDataTypes.INCIDENTCLASSIFICATIONS) {
        //No longer editable
      }
    }
    // Save name-value
    else if (props.listType == 'name-value') {
      if (!itemValue) {
        setShowModalErrors(true);
        return;
      }
      if (props.listName == ListDataTypes.SEVERITYLEVELS) {
        //no longer editable
      } else if (props.listName == ListDataTypes.INCIDENTPROBABILITIES) {
        //no longer editable
      }
    }
    // Save basic
    else {
      if (addingRecord) {
        newRecord = {
          id: new ObjectId().toString(),
          isActive: modalRecord.isActive,
          name: modalRecord.name,
          partition: modalRecord.partition,
          sortOrder: modalRecord.sortOrder,
          SQLServerId: modalRecord.SQLServerId,
        };
        await createListData(props.listName, newRecord);
      } else {
        newRecord = {
          id: modalRecord._id,
          isActive: modalRecord.isActive,
          name: modalRecord.name,
          sortOrder: modalRecord.sortOrder,
        };
        await updateListData(props.listName, newRecord);
      }
    }

    toast.show('Record saved successfully', {type: 'success'});
    setAddingRecord(false);
    setModalRecord(null);
    setShowModalErrors(false);
    setShowModal(false);
    setSelectedRecord(null);
    fetchRecords(newRecord);
  }

  const renderRecords = (): React.ReactElement => {
    let filteredRecords = records.filter(
      r =>
        // Search filter
        (search === '' ||
          r.name?.toLowerCase().includes(search.toLowerCase())) &&
        // Show inactive filter
        (r.isActive || showInactive),
    );

    if (filteredRecords.length === 0)
      return (
        <View style={ReportsStyleSheet.list}>
          <Text
            style={[
              ProjectSitesStyleSheet.siteItemText,
              {textAlign: 'center', color: Colors.darkestGreen},
            ]}>
            No Results Found
          </Text>
        </View>
      );

    return (
      <View style={ReportsStyleSheet.list}>
        <View
          style={{
            flexDirection: 'row',
            borderBottomWidth: 0.4,
            borderBottomColor: Colors.darkGreen,
          }}>
          <View style={[ReportsStyleSheet.listItemContainer, {flex: 1}]}>
            <Text
              style={[
                ReportsStyleSheet.listItemName,
                {
                  flex: 3,
                  fontFamily: 'Poppins',
                  textTransform: 'uppercase',
                },
              ]}
              numberOfLines={2}>
              Name
            </Text>
            {props.listType == 'master-detail' && (
              <Text
                style={[
                  ReportsStyleSheet.listItemName,
                  {
                    flex: 1,
                    fontFamily: 'Poppins',
                    textTransform: 'uppercase',
                    textAlign: 'center',
                  },
                ]}
                numberOfLines={2}>
                {masterLabel}
              </Text>
            )}
            {!props.isReadOnly && (
              <>
                <Text
                  style={[
                    ReportsStyleSheet.listItemName,
                    {
                      flex: 1,
                      fontFamily: 'Poppins',
                      textTransform: 'uppercase',
                      textAlign: 'center',
                    },
                  ]}
                  numberOfLines={2}>
                  Order
                </Text>
                <Text
                  style={[
                    ReportsStyleSheet.listItemName,
                    {
                      flex: 1,
                      fontFamily: 'Poppins',
                      textTransform: 'uppercase',
                      textAlign: 'center',
                    },
                  ]}
                  numberOfLines={2}>
                  Is Active
                </Text>
              </>
            )}
          </View>
          <View style={{width: 49}}></View>
        </View>
        {listPageData.map((record, index) => {
          return renderRecord(record, index);
        })}
        {filteredRecords.length > 0 && (
          <View style={{marginTop: 24}}>
            <Paginator
              key='listPaginator'
              data={filteredRecords}
              pageSize={DISPLAY_INCREMENT}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              searchDeps={[records, search]}
              onPaginatedDataChange={setListPageData}
            />
          </View>
        )}
      </View>
    );
  };

  const renderRecord = (
    record: GenericListItem,
    index: number,
  ): React.ReactElement => {
    return (
      <View
        key={index}
        style={{
          flexDirection: 'row',
          borderBottomWidth: 0.4,
          borderBottomColor: Colors.darkGreen,
        }}>
        <Pressable
          style={({pressed}) => [
            ReportsStyleSheet.listItemContainer,
            {flex: 1},
            pressed && {
              backgroundColor: Colors.darkGreenTransparent,
            },
          ]}
          onPress={() => setSelectedRecord(record)}>
          <Text
            style={[ReportsStyleSheet.listItemName, {flex: 3}]}
            numberOfLines={2}>
            {record.name}
          </Text>
          {props.listType == 'master-detail' && (
            <Text
              style={[
                ReportsStyleSheet.listItemName,
                {flex: 1, textAlign: 'center'},
              ]}
              numberOfLines={2}>
              {record.masterLabel}
            </Text>
          )}
          {!props.isReadOnly && (
            <>
              <Text
                style={[
                  ReportsStyleSheet.listItemName,
                  {flex: 1, textAlign: 'center'},
                ]}
                numberOfLines={2}>
                {record.sortOrder}
              </Text>
              <Text
                style={[
                  ReportsStyleSheet.listItemName,
                  {flex: 1, textAlign: 'center'},
                ]}
                numberOfLines={2}>
                {record.isActive ? 'Active' : 'Inactive'}
              </Text>
            </>
          )}
        </Pressable>
        {renderActions(record)}
      </View>
    );
  };

  const renderActions = (record: GenericListItem): React.ReactElement => {
    if (props.isReadOnly) return <></>;

    return (
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          width: 49,
        }}>
        {selectedRecord && selectedRecord._id === record._id && (
          <Pressable
            style={({pressed}) => [
              pressed && {
                opacity: 0.6,
              },
              ReportsStyleSheet.draftAction,
            ]}
            onPress={viewRecord}>
            <Icon icon={'eye'} color={Colors.white} size={20} />
          </Pressable>
        )}
      </View>
    );
  };

  const renderModal = (): React.ReactElement => {
    return (
      <Modal
        visible={showModal}
        transparent={true}
        statusBarTranslucent={true}
        animationType='fade'>
        <View style={ControlsStyleSheet.modalBackground}>
          <View style={ControlsStyleSheet.groupSelectorModal}>
            <View style={ControlsStyleSheet.groupSelectorModalBar}>
              <Text style={ControlsStyleSheet.groupSelectorModalTitle}>
                {addingRecord ? 'Add Record' : 'Record View'}
              </Text>
              <Pressable
                style={({pressed}) => [
                  ControlsStyleSheet.groupSelectorModalClose,
                  pressed && {
                    backgroundColor: Colors.darkGreenTransparent,
                    borderRadius: 24,
                  },
                ]}
                onPress={closeModal}>
                <Icon icon={'close'} color={Colors.darkestGreen} size={24} />
              </Pressable>
            </View>
            <ScrollView
              keyboardShouldPersistTaps='handled'
              style={{marginVertical: 36, marginHorizontal: 18}}>
              {modalRecord ? (
                <View>
                  <DynamicTextBox
                    config={{
                      required: true,
                      maxLength: 150,
                    }}
                    label='Name'
                    value={modalRecord.name}
                    onChange={(controlId, controlTypeId, value, isValid) => {
                      onModalRecordChange('name', value);
                    }}
                    disabled={props.isReadOnly || !canUpsert}
                    showError={showModalErrors}
                  />
                  <Text style={[ControlsStyleSheet.error, {top: -20}]}>
                    {nameError}
                  </Text>
                  {props.listType == 'name-value' && (
                    <DynamicTextBox
                      config={{
                        required: true,
                        maxLength: 150,
                      }}
                      label='Value'
                      value={itemValue}
                      onChange={(controlId, controlTypeId, value, isValid) => {
                        setItemValue(value);
                      }}
                      disabled={!canUpsert}
                      showError={showModalErrors}
                    />
                  )}
                  <DynamicTextBox
                    config={{
                      required: true,
                      validationRegExp: RegexValidation.NUMBER_ONLY,
                      maxLength: 3,
                      placeholder: '0-999',
                    }}
                    label='Sort Order'
                    value={sortOrderValue}
                    onChange={(controlId, controlTypeId, value, isValid) => {
                      setSortOrderValue(value);
                    }}
                    disabled={props.isReadOnly || !canUpsert}
                    showError={showModalErrors}
                  />
                  {props.listType == 'master-detail' && masterSource && (
                    <DynamicSelect
                      label={masterLabel}
                      config={{required: true, optionSource: masterSource}}
                      value={masterValue}
                      onChange={(controlId, controlTypeId, value, isValid) =>
                        setMasterValue(value)
                      }
                      disabled={!canUpsert}
                      showError={showModalErrors}
                    />
                  )}
                  <DynamicSegmentedControl
                    config={{segments: [{name: 'Yes'}, {name: 'No'}]}}
                    label='Is Active'
                    value={modalRecord.isActive ? 'Yes' : 'No'}
                    onChange={(controlId, controlTypeId, value, isValid) => {
                      onModalRecordChange('isActive', value);
                    }}
                    disabled={props.isReadOnly || !canUpsert}
                  />
                </View>
              ) : null}
            </ScrollView>
            {!props.isReadOnly && canUpsert && (
              <Pressable
                style={({pressed}) => [
                  CommonStyleSheet.greenButton,
                  {alignSelf: 'center', marginTop: 'auto', marginBottom: 24},
                  pressed && {
                    backgroundColor: Colors.darkGreenTransparent,
                  },
                ]}
                onPress={() => saveRecord()}>
                <Text style={CommonStyleSheet.greenButtonText}>
                  {addingRecord ? 'Add Record' : 'Save'}
                </Text>
              </Pressable>
            )}
          </View>
        </View>
      </Modal>
    );
  };

  const renderOIICSLink = (): React.ReactElement => {
    let link = '';
    if (props.listName == ListDataTypes.BODYPARTS) link = OIICS_LINKS.BODYPARTS;
    else if (props.listName == ListDataTypes.MECHANISMS)
      link = OIICS_LINKS.MECHANISMS;
    else if (props.listName == ListDataTypes.SOURCES)
      link = OIICS_LINKS.SOURCES;
    else if (props.listName == ListDataTypes.NATURES)
      link = OIICS_LINKS.NATURES;

    if (!link) return <></>;

    return (
      <DynamicLinkText
        key={-1}
        controlId={-1}
        controlTypeId={-1}
        config={{href: link, linkText: 'Click here to view standards'}}
        visible={true}
      />
    );
  };

  return (
    <ScrollView>
      <View style={{height: 260, backgroundColor: Colors.darkestGreen}}>
        <Pressable
          hitSlop={40}
          style={({pressed}) => [
            {
              width: 42,
              height: 42,
              borderRadius: 42,
              padding: 5,
            },
            pressed && {
              backgroundColor: Colors.teal,
            },
          ]}
          onPress={() => goBack()}>
          <img
            src={`data:image/svg+xml;utf8,${encodeURIComponent(
              Icons.CHEVRON_LEFT.replaceAll('currentColor', Colors.white),
            )}`}
          />
        </Pressable>
        <Text style={ReportsStyleSheet.headerTitle}>{props.listLabel}</Text>
      </View>
      <View style={{flexDirection: 'row'}}>
        <View style={{flex: 2, padding: 28}}>
          {!props.isReadOnly && canUpsert && (
            <View style={{marginTop: -58, marginLeft: 'auto', marginRight: 28}}>
              <CustomPressable
                style={({pressed}) => [
                  {
                    backgroundColor: Colors.green,
                    width: 58,
                    height: 58,
                    borderRadius: 58,
                  },
                  pressed && {
                    backgroundColor: Colors.teal,
                  },
                ]}
                onPress={addRecord}
                tooltip='Add New'>
                <View style={{marginTop: -1, marginLeft: -1}}>
                  <Icon icon={'add'} color={Colors.white} size={60} />
                </View>
              </CustomPressable>
            </View>
          )}
          <SearchBox onChangeSearch={newSearch => setSearch(newSearch)} />
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginHorizontal: 20,
              marginTop: 6,
            }}>
            <View
              style={{
                alignSelf: 'flex-start',
                paddingTop: 5,
              }}>
              {renderOIICSLink()}
            </View>
            <View
              style={{
                alignSelf: 'flex-end',
              }}>
              {!props.isReadOnly && (
                <DynamicCheckBox
                  label='Show Inactive'
                  value={showInactive ? 'true' : 'false'}
                  onChange={(controlId, controlTypeId, value) =>
                    setShowInactive(value === 'true')
                  }
                />
              )}
            </View>
          </View>
          {renderRecords()}
        </View>
        <View style={{flex: 1}}>
          <View style={CommonStyleSheet.bcsfFooter}>
            <Image
              source={Images.BCSF}
              style={CommonStyleSheet.bcsfFooterImage}
            />
            <Text style={CommonStyleSheet.bcsfFooterText}>
              BC <Text style={{fontWeight: '700'}}>Forest Safety</Text>
            </Text>
          </View>
        </View>
      </View>
      {renderModal()}
    </ScrollView>
  );
};

export default EditListManagement;
